import { useState, useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import Masonry from 'react-masonry-css';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  STATISTICS_MASONRY_BREAKPOINTS,
  StatisticsChartsMasonry,
} from './statisticsChartsMasonry';
import PageHeader from '../PageHeader';
import { slateColors } from '../ContentPages/AppLandingPage/styles';
import { LineStats } from './Types/lineStats';
import { DelayHistory } from './Types/delayHistory';
import { SegmentedControlM } from '../Controls/Buttons';
import AvgDelayCard from './AvgDelayCard';
import DelayHistoryCard from './DelayHistoryCard';
import DistributionCard from './DelayDistributionCard';
import DelayEvolutionCard from './DelayEvolutionCard';
import FeedbackCard from '../Elements/FeedbackCard';
import { getApi } from '../../utils/api';
import { getLineName } from '../../utils/mapJourneysData';
import { useTranslation } from 'react-i18next';
import {
  deserializeTrainStatsFromSearchParams,
  serializeTrainStatsToSearchParams,
  buildLegStubFromTrainUrlPayload,
  trainStatsPayloadFromLeg,
} from '../../utils/trainStatsUrlParams';

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  padding: 24px 16px;
  gap: 32px;
  @media (min-width: 768px) {
    padding: 36px 64px; // Increased horizontal padding for desktop
  }
`;

const ChartsContainer = StatisticsChartsMasonry;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  align-self: stretch;
  padding: 1rem;
  background: white;
  border-bottom: 2px solid ${slateColors[200]};

  @media (min-width: 768px) {
    padding: 1.5rem 2rem;
    gap: 0.75rem;
  }
`;

const SegmentedControlContainer = styled('div')`
  display: flex;
  align-items: center;
  align-content: center;
  gap: 8px;
  align-self: stretch;
  flex-wrap: wrap;
`;

type LineStatisticsResponse = LineStats[];

type DelayHistoryResponse = DelayHistory[];

const TrainStatistics = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlPayload = useMemo(
    () => deserializeTrainStatsFromSearchParams(searchParams),
    [searchParams]
  );

  const legFromState = location.state?.leg;
  const fromPath = (location.state?.from as string) || '';

  const leg = useMemo(() => {
    if (urlPayload) {
      return buildLegStubFromTrainUrlPayload(urlPayload);
    }
    return legFromState ?? null;
  }, [urlPayload, legFromState]);

  /**
   * Breadcrumbs: shared URLs have no `location.state.from` → only Home + title (no dead links).
   * In-app, `from` is set (e.g. `/journey/:id`) → show Journeys + Journey details.
   */
  const trainBreadcrumbs = useMemo(() => {
    const crumbs: Array<{ label: string; path?: string; active?: boolean }> = [
      { label: t('common.home'), path: '/' },
    ];
    const inApp = Boolean(fromPath);
    if (inApp) {
      crumbs.push({ label: t('pageHeader.journeys'), path: '/journeys' });
    }
    if (fromPath.startsWith('/journey/')) {
      const jid = fromPath.split('/journey/')[1]?.split('/')[0];
      if (jid) {
        crumbs.push({
          label: t('statistics.trainStatistics.journeyDetails'),
          path: `/journey/${jid}`,
        });
      }
    }
    crumbs.push({ label: t('statistics.trainStatistics.title'), active: true });
    return crumbs;
  }, [fromPath, t]);

  const hasSyncedUrlRef = useRef(false);
  useEffect(() => {
    if (deserializeTrainStatsFromSearchParams(searchParams)) return;
    if (hasSyncedUrlRef.current) return;
    if (!legFromState) return;
    hasSyncedUrlRef.current = true;
    setSearchParams(serializeTrainStatsToSearchParams(trainStatsPayloadFromLeg(legFromState)), {
      replace: true,
    });
  }, [searchParams, legFromState, setSearchParams]);

  const [selectedOption, setSelectedOption] = useState<'origin' | 'destination'>('destination');
  const [lineStatsData, setlineStatsData] = useState<LineStatisticsResponse | null>(null);
  const [delayHistoryData, setDelayHistoryData] = useState<DelayHistoryResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const hasLeg = Boolean(leg);

  useEffect(() => {
    if (!leg) {
      setlineStatsData(null);
      setDelayHistoryData(null);
      setLoading(false);
      return;
    }

    const stopId = selectedOption === 'origin' ? leg.origin.id : leg.destination.id;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const lineStatsPromise = getApi('linestats', { lineName: leg.line.name, stopId });
        const delayHistoryPromise = getApi('delayhistory', { lineName: leg.line.name, stopId });

        const [lineStatsResult, delayHistoryResult] = await Promise.all([
          lineStatsPromise,
          delayHistoryPromise,
        ]);

        setlineStatsData(lineStatsResult);
        setDelayHistoryData(delayHistoryResult);
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unknown error occurred');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [leg, selectedOption]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const helmetTitle = leg
    ? `${getLineName(leg)}: ${leg.origin.name} → ${leg.destination.name} | Train Statistics | chuuchuu`
    : 'Train Statistics | chuuchuu';
  const helmetDesc = leg
    ? `Historical punctuality data for ${getLineName(leg)} between ${leg.origin.name} and ${leg.destination.name}. See delay distributions, trends, and on-time performance.`
    : 'Explore historical delay data and punctuality trends for European trains.';

  return (
    <>
      <Helmet>
        <title>{helmetTitle}</title>
        <meta name="description" content={helmetDesc} />
      </Helmet>
      <HeaderContainer>
        <PageHeader
          breadcrumbs={trainBreadcrumbs}
          title={t('statistics.trainStatistics.title')}
          subtitle={
            hasLeg
              ? `${getLineName(leg)} - ${leg.origin.name} ➜ ${leg.destination.name}`
              : ''
          }
        />
      </HeaderContainer>
      <Container>
        {!hasLeg && (
          <div>{t('statistics.trainStatistics.missingLinkParams')}</div>
        )}
        {hasLeg && (
          <SegmentedControlContainer>
            <SegmentedControlM
              activeValue={selectedOption}
              options={[
                {
                  label: `${t('statistics.trainStatistics.departureAtPlace')} ${leg.origin.name}`,
                  value: 'origin' as const,
                },
                {
                  label: `${t('statistics.trainStatistics.arrivalAtPlace')} ${leg.destination.name}`,
                  value: 'destination' as const,
                },
              ]}
              onChange={(value) => setSelectedOption(value)}
            />
          </SegmentedControlContainer>
        )}
        {loading && <div>{t('common.loading')}</div>}
        {error && (
          <div>
            {t('common.error')}: {error}
          </div>
        )}
        <ChartsContainer>
          <Masonry
            breakpointCols={STATISTICS_MASONRY_BREAKPOINTS}
            className="statistics-masonry-grid"
            columnClassName="statistics-masonry-grid_column"
          >
            {delayHistoryData && (
              <DistributionCard
                delayHistoryData={delayHistoryData}
                selectedOption={selectedOption as 'origin' | 'destination'}
              />
            )}
            {delayHistoryData && (
              <DelayEvolutionCard
                delayHistoryData={delayHistoryData}
                selectedOption={selectedOption as 'origin' | 'destination'}
              />
            )}
            {delayHistoryData && (
              <DelayHistoryCard
                delayHistoryData={delayHistoryData}
                selectedOption={selectedOption as 'origin' | 'destination'}
              />
            )}
            {lineStatsData && <AvgDelayCard data={lineStatsData} />}
            {hasLeg && (
              <FeedbackCard
                source={`${getLineName(leg)} statistics from ${leg.origin.name} to ${leg.destination.name}`}
              />
            )}
          </Masonry>
        </ChartsContainer>
      </Container>
    </>
  );
};

export default TrainStatistics;
